import React, { useCallback } from 'react';
import { LockOutlined } from '@ant-design/icons';
import { Alert, Button, Form, Input, Typography, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { forgotPassword } from './ForgotPasswordServices';
import { useGlobal } from '../Shared/GlobalContext/GlobalContext';
import CommonWrapper from '../Shared/CommonWrapper/CommonWrapper';
import Turnstile from 'react-turnstile';
import { useTheme } from '../Shared/GlobalContext/ThemeContext';
import useTurnstileScale from '../../Hooks/useTurnstileScale';
import i18n from '../../i18n';
import { mapTurnstileLanguage } from '../../Utils/turnstile';
import { useErrorHandler } from '../../Hooks/useErrorHandler';

const { Text } = Typography;

const ForgotPassword = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { loading, setLoading } = useGlobal();
    const [form] = Form.useForm();
    const { handleError } = useErrorHandler();
    const [captchaToken, setCaptchaToken] = React.useState('');
    const [submittedEmail, setSubmittedEmail] = React.useState('');
    const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;
    const { isDarkMode } = useTheme();
    const turnstileRef = React.useRef(null);
    const turnstileWidgetRef = React.useRef(null);
    useTurnstileScale(turnstileRef, {}, [isDarkMode]);
    const captchaLang = mapTurnstileLanguage(i18n.language);

    const resetCaptcha = useCallback(() => {
        setCaptchaToken('');
        turnstileWidgetRef.current?.reset?.();
    }, []);

    const onFinish = useCallback(async (values) => {
        try {
            setLoading(true);
            if (!captchaToken) {
                message.error(t('please_verify_captcha'));
                return;
            }
            await forgotPassword(values.email, captchaToken);
            setSubmittedEmail(values.email);
            resetCaptcha();
        } catch (error) {
            handleError(error, form);
            resetCaptcha();
        } finally {
            setLoading(false);
        }
    }, [setLoading, captchaToken, t, handleError, form, resetCaptcha]);

    const handleLoginRedirect = () => {
        navigate('/login');
    };

    return (
        <CommonWrapper forceOverflowScroll>
            {submittedEmail ? (
                <>
                    <Alert
                        type="success"
                        showIcon
                        message={t('password_reset_email_sent_title')}
                        description={t('password_reset_email_sent_description', { email: submittedEmail })}
                    />
                    <Button
                        block
                        type="primary"
                        size="large"
                        onClick={handleLoginRedirect}
                        style={{ marginTop: 24 }}
                    >
                        {t('go_back_to_login')}
                    </Button>
                </>
            ) : (
                <Form
                    form={form}
                    name="forgot-password"
                    style={{ maxWidth: '100%', margin: '0 auto' }}
                    onFinish={onFinish}
                >
                    <Form.Item
                        name="email"
                        rules={[
                            { required: true, message: t('please_input_your_email') },
                            { type: 'email', message: t('please_enter_a_valid_email') }
                        ]}
                    >
                        <Input
                            prefix={<LockOutlined />}
                            placeholder={t('email')}
                            size="large"
                            style={{ borderRadius: '5px' }}
                        />
                    </Form.Item>
                    {siteKey && (
                        <Turnstile
                            key={`turnstile-${isDarkMode ? 'dark' : 'light'}`}
                            sitekey={siteKey}
                            appearance="always"
                            theme={isDarkMode ? 'dark' : 'light'}
                            language={captchaLang}
                            refreshExpired="auto"
                            className="turnstile-embed"
                            style={{ width: '100%' }}
                            userRef={turnstileRef}
                            responseField={false}
                            onLoad={(_, boundTurnstile) => {
                                turnstileWidgetRef.current = boundTurnstile;
                            }}
                            onVerify={(token, boundTurnstile) => {
                                turnstileWidgetRef.current = boundTurnstile;
                                setCaptchaToken(token);
                            }}
                            onExpire={() => setCaptchaToken('')}
                            onError={() => setCaptchaToken('')}
                        />
                    )}
                    <Form.Item style={{ marginTop: 24 }}>
                        <Button
                            block
                            type="primary"
                            htmlType="submit"
                            size="large"
                            loading={loading}
                        >
                            {t('reset_password')}
                        </Button>
                    </Form.Item>
                    <div style={{ textAlign: 'center', marginTop: '10px' }}>
                        <Text>{t('remember_your_password')} </Text>
                        <Button
                            type="link"
                            style={{ fontSize: '0.9em', padding: 0 }}
                            onClick={handleLoginRedirect}
                        >
                            {t('go_back_to_login')}
                        </Button>
                    </div>
                </Form>
            )}
        </CommonWrapper>
    );
};

export default ForgotPassword;
